import React, { useEffect, useState } from 'react';
import { apiClient } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { TrendingUp, XCircle, Clock, RotateCcw, ArrowUpRight } from 'lucide-react';
import logger from '@/lib/logger';

/**
 * CEO/SM widget showing conversion outcomes of leads pulled from the pool.
 *
 * For each lead where any assignment_history entry has a "pool pull" action
 * in the last N days, we classify the current stage as:
 *   - enrolled          → closed 🎯
 *   - rejected_again    → agent gave up 💔
 *   - in_progress       → still working the deal ⏳
 *
 * Also shows a leaderboard of the managers who did the pulls so it's easy
 * to see whose reassignments are converting.
 */
export const PoolAssignmentOutcomeCard = () => {
    const [data, setData] = useState(null);
    const [days, setDays] = useState(30);
    const [loading, setLoading] = useState(true);

    const fetch = async (d) => {
        setLoading(true);
        try {
            const r = await apiClient.get(`/admin/pool-assignment-outcome?days=${d}`);
            setData(r.data);
        } catch (e) {
            logger.error('pool-assignment-outcome failed', e);
            setData({ totals: {}, by_puller: [] });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetch(days); }, [days]);

    const totals = data?.totals || {};
    const pullers = data?.by_puller || [];

    return (
        <Card data-testid="pool-assignment-outcome-card">
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <RotateCcw className="h-5 w-5 text-blue-500" />
                        <CardTitle className="text-base">Pool Assignment Outcome</CardTitle>
                    </div>
                    <div className="flex items-center gap-1">
                        {[7, 30, 90].map(d => (
                            <button
                                key={d}
                                onClick={() => setDays(d)}
                                className={`text-[11px] px-2 py-0.5 rounded-md transition-colors ${
                                    days === d
                                        ? 'bg-blue-500/15 text-blue-500 font-medium'
                                        : 'text-muted-foreground hover:text-foreground'
                                }`}
                                data-testid={`pool-outcome-days-${d}`}
                            >
                                {d}d
                            </button>
                        ))}
                    </div>
                </div>
                <CardDescription className="text-xs">
                    Outcome of leads pulled from the pool by managers in the last {days} days.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {loading ? (
                    <div className="h-32 animate-pulse bg-muted/40 rounded" />
                ) : (
                    <>
                        {/* KPI strip */}
                        <div className="grid grid-cols-4 gap-2">
                            <div className="rounded-md border p-2.5 bg-emerald-500/5" data-testid="pool-outcome-enrolled">
                                <div className="flex items-center gap-1.5 text-emerald-500 mb-0.5">
                                    <TrendingUp className="h-3.5 w-3.5" />
                                    <span className="text-[10px] uppercase tracking-wide font-medium">Enrolled</span>
                                </div>
                                <div className="text-2xl font-bold tabular-nums">{(totals.enrolled || 0).toLocaleString()}</div>
                                <div className="text-[10px] text-muted-foreground">{totals.conversion_rate || 0}% conv</div>
                            </div>
                            <div className="rounded-md border p-2.5 bg-blue-500/5" data-testid="pool-outcome-in-progress">
                                <div className="flex items-center gap-1.5 text-blue-500 mb-0.5">
                                    <Clock className="h-3.5 w-3.5" />
                                    <span className="text-[10px] uppercase tracking-wide font-medium">In Progress</span>
                                </div>
                                <div className="text-2xl font-bold tabular-nums">{(totals.in_progress || 0).toLocaleString()}</div>
                                <div className="text-[10px] text-muted-foreground">still working</div>
                            </div>
                            <div className="rounded-md border p-2.5 bg-rose-500/5" data-testid="pool-outcome-rejected">
                                <div className="flex items-center gap-1.5 text-rose-500 mb-0.5">
                                    <XCircle className="h-3.5 w-3.5" />
                                    <span className="text-[10px] uppercase tracking-wide font-medium">Rejected Again</span>
                                </div>
                                <div className="text-2xl font-bold tabular-nums">{(totals.rejected_again || 0).toLocaleString()}</div>
                                <div className="text-[10px] text-muted-foreground">{totals.rejection_rate || 0}% rate</div>
                            </div>
                            <div className="rounded-md border p-2.5 bg-amber-500/5" data-testid="pool-outcome-revenue">
                                <div className="flex items-center gap-1.5 text-amber-500 mb-0.5">
                                    <ArrowUpRight className="h-3.5 w-3.5" />
                                    <span className="text-[10px] uppercase tracking-wide font-medium">Revenue</span>
                                </div>
                                <div className="text-2xl font-bold tabular-nums">
                                    AED {(totals.revenue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                </div>
                                <div className="text-[10px] text-muted-foreground">from enrolled pulls</div>
                            </div>
                        </div>

                        {/* Leaderboard */}
                        {pullers.length > 0 ? (
                            <div className="border rounded-md overflow-hidden">
                                <Table>
                                    <TableHeader className="bg-muted/40">
                                        <TableRow>
                                            <TableHead className="text-xs">Manager</TableHead>
                                            <TableHead className="text-right text-xs">Enrolled</TableHead>
                                            <TableHead className="text-right text-xs">In Progress</TableHead>
                                            <TableHead className="text-right text-xs">Rejected</TableHead>
                                            <TableHead className="text-right text-xs">Conv %</TableHead>
                                            <TableHead className="text-right text-xs">Revenue</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {pullers.slice(0, 10).map((p, idx) => (
                                            <TableRow
                                                key={p.puller_id || idx}
                                                data-testid={`pool-outcome-puller-${idx}`}
                                            >
                                                <TableCell className="text-xs">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="font-medium">{p.puller_name}</span>
                                                        {p.puller_role && (
                                                            <Badge variant="outline" className="text-[9px] px-1 py-0 font-normal">
                                                                {p.puller_role.replace('_', ' ')}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                </TableCell>
                                                <TableCell className="text-right text-xs font-mono text-emerald-500">{p.enrolled}</TableCell>
                                                <TableCell className="text-right text-xs font-mono text-muted-foreground">{p.in_progress}</TableCell>
                                                <TableCell className="text-right text-xs font-mono text-rose-500">{p.rejected_again}</TableCell>
                                                <TableCell className="text-right text-xs font-mono">{p.conversion_rate}%</TableCell>
                                                <TableCell className="text-right text-xs font-mono">
                                                    AED {(p.revenue || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        ) : (
                            <div className="text-center py-8 text-sm text-muted-foreground">
                                No pool pulls in the last {days} days.
                            </div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    );
};

export default PoolAssignmentOutcomeCard;
